import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import AppButton from "../../../ui/buttons/AppButton";
import ModalShell from "../../../ui/overlays/ModalShell";
import { DraftGuardContext, type DirtyEditor } from "./draftGuardContext";

interface PendingClose {
  readonly close: () => void;
  readonly cancel: () => void;
  readonly id?: string;
}

/** One router blocker for every editor, including editors nested in a drawer. */
export default function UnsavedDraftGuard({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const editors = useRef(new Map<string, DirtyEditor>());
  const [dirty, setDirty] = useState(false);
  // The dirty editors that can save: "Save as draft" is offered only for them.
  const [saveableDirty, setSaveableDirty] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<PendingClose | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const bypass = useRef(false);
  // One guard per work tab (F90): it guards that tab's navigation, and the owner asks it before the tab closes.
  // It asks the editors as they are when the navigation comes, so a save or an unmount just
  // committed counts before this guard renders again.
  const blocker = useBlocker(() => !bypass.current && [...editors.current.values()].some((entry) => entry.dirty));
  const register = useCallback((id: string, editor: DirtyEditor) => {
    const refresh = () => {
      setDirty([...editors.current.values()].some((entry) => entry.dirty));
      setSaveableDirty(new Set([...editors.current].filter(([, entry]) => entry.dirty && entry.saveable).map(([key]) => key)));
    };
    editors.current.set(id, editor);
    refresh();
    return () => {
      editors.current.delete(id);
      refresh();
    };
  }, []);
  const requestClose = useCallback((close: () => void, id?: string) => {
    if ([...editors.current].some(([key, entry]) => entry.dirty && (id === undefined || key === id))) {
      setPending({ close, cancel: () => undefined, ...(id === undefined ? {} : { id }) });
    } else {
      close();
    }
  }, []);
  const context = useMemo(() => ({ register, requestClose }), [register, requestClose]);
  const navigation = blocker.state === "blocked" ? { close: blocker.proceed, cancel: blocker.reset } : null;
  const active = pending ?? navigation;

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => { window.removeEventListener("beforeunload", warn); };
  }, [dirty]);

  const keep = () => {
    active?.cancel();
    setSaveFailed(false);
    setPending(null);
  };
  const discard = () => {
    setSaveFailed(false);
    setPending(null);
    bypass.current = true;
    try { active?.close(); } finally { bypass.current = false; }
  };
  const save = async () => {
    if (active === null) return;
    setSaveFailed(false);
    setSaving(true);
    // Save the innermost editor first. Saving a step can update its Workflow draft.
    const ids = [...editors.current].sort(([, left], [, right]) => right.priority - left.priority).map(([id]) => id);
    for (const id of ids) {
      const editor = editors.current.get(id);
      if (editor?.dirty && (!("id" in active) || active.id === id)) {
        if (!await editor.save()) {
          setSaveFailed(true);
          setSaving(false);
          return;
        }
        // Let the saved callback and any parent changes render before reading the next editor.
        flushSync(() => { setSaving(true); });
      }
    }
    if ([...editors.current].some(([id, editor]) => editor.dirty && (!("id" in active) || active.id === id))) {
      setSaveFailed(true);
      setSaving(false);
      return;
    }
    setSaving(false);
    setSaveFailed(false);
    setPending(null);
    bypass.current = true;
    try { active.close(); } finally { bypass.current = false; }
  };

  // "Save as draft" only where an editor in question can save; a report can be kept or dropped.
  const canSave = active !== null && ("id" in active && active.id !== undefined ? saveableDirty.has(active.id) : saveableDirty.size > 0);

  return (
    <DraftGuardContext.Provider value={context}>
      <fieldset disabled={saving} className="contents">{children}</fieldset>
      {active === null ? null : (
        <ModalShell title={t("authoring.unsaved.title")} onClose={keep} closeDisabled={saving} size="md" zIndexClassName="z-[60]"
          footer={<div className="flex justify-end gap-2">
            <AppButton variant="secondary" size="sm" disabled={saving} onClick={keep}>{t("authoring.unsaved.keep")}</AppButton>
            <AppButton variant="danger" size="sm" disabled={saving} onClick={discard}>{t("authoring.unsaved.drop")}</AppButton>
            {canSave ? <AppButton variant="primary" size="sm" disabled={saving} onClick={() => { void save(); }}>{t("authoring.unsaved.save")}</AppButton> : null}
          </div>}
        >
          <p className="text-sm">{t("authoring.unsaved.body")}</p>
          {saveFailed ? <p role="alert" className="app-error-text text-sm">{t("authoring.unsaved.saveFailed")}</p> : null}
        </ModalShell>
      )}
    </DraftGuardContext.Provider>
  );
}
